const SALES_DRAFT_STORAGE_PREFIX = "elsa:sales-replenishment-draft:";
const SALES_DRAFT_VERSION = 1;
export const SALES_REPLENISHMENT_DRAFT_TTL_MS = 8 * 60 * 60 * 1000;

const ALLOWED_SOURCE_TYPES = new Set(["pos", "sale"]);
const FORBIDDEN_PAYMENT_KEYS = new Set([
  "amountreceived",
  "initialpayment",
  "initialpaymentenabled",
  "paidamount",
  "payment",
  "paymentmode",
  "paymentmodeid",
  "payments",
  "paymentstatus",
  "selectedmodepaiement",
  "treasuryaccountid",
]);

const toPositiveInt = (value) => {
  const parsedValue = Number(value?.id ?? value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const toPositiveQuantity = (value) => {
  const normalizedValue =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? Number(parsedValue.toFixed(5))
    : null;
};

const normalizeStorageKey = (key) =>
  String(key || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const sanitizePayload = (value) => {
  if (Array.isArray(value)) {
    return value.map(sanitizePayload);
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((result, [key, entryValue]) => {
    if (FORBIDDEN_PAYMENT_KEYS.has(normalizeStorageKey(key))) {
      return result;
    }
    result[key] = sanitizePayload(entryValue);
    return result;
  }, {});
};

const getSessionStorage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
};

const isValidDraftId = (draftId) =>
  typeof draftId === "string" && /^[a-z0-9-]{8,100}$/i.test(draftId);

const buildDraftId = () => {
  const browserCrypto =
    typeof window !== "undefined" ? window.crypto : null;
  if (typeof browserCrypto?.randomUUID === "function") {
    return browserCrypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const removeExpiredDrafts = (storage, now) => {
  if (!storage) return;

  try {
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(SALES_DRAFT_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }

    keys.forEach((key) => {
      try {
        const draft = JSON.parse(storage.getItem(key) || "null");
        if (!draft?.expires_at || Number(draft.expires_at) <= now) {
          storage.removeItem(key);
        }
      } catch (_error) {
        storage.removeItem(key);
      }
    });
  } catch (_error) {
    // Le stockage temporaire reste une optimisation : l'appelant gère l'échec.
  }
};

export const saveSalesReplenishmentDraft = ({
  sourceType,
  sourcePath,
  userId,
  companyId,
  warehouseId,
  payload,
  now = Date.now(),
  ttlMs = SALES_REPLENISHMENT_DRAFT_TTL_MS,
} = {}) => {
  const normalizedSourceType = String(sourceType || "").toLowerCase();
  const normalizedUserId = toPositiveInt(userId);
  const normalizedCompanyId = toPositiveInt(companyId);
  const normalizedWarehouseId = toPositiveInt(warehouseId);
  const storage = getSessionStorage();

  if (
    !storage ||
    !ALLOWED_SOURCE_TYPES.has(normalizedSourceType) ||
    !normalizedUserId ||
    !normalizedCompanyId ||
    !normalizedWarehouseId ||
    !payload ||
    typeof payload !== "object"
  ) {
    return null;
  }

  try {
    removeExpiredDrafts(storage, now);
    const draft = {
      version: SALES_DRAFT_VERSION,
      id: buildDraftId(),
      source_type: normalizedSourceType,
      source_path: String(sourcePath || ""),
      user_id: normalizedUserId,
      company_id: normalizedCompanyId,
      warehouse_id: normalizedWarehouseId,
      created_at: now,
      expires_at: now + Math.max(Number(ttlMs) || 0, 1),
      payload: sanitizePayload(payload),
    };

    storage.setItem(
      `${SALES_DRAFT_STORAGE_PREFIX}${draft.id}`,
      JSON.stringify(draft)
    );
    return draft;
  } catch (_error) {
    return null;
  }
};

export const loadSalesReplenishmentDraft = (
  draftId,
  { now = Date.now() } = {}
) => {
  const storage = getSessionStorage();
  if (!storage || !isValidDraftId(draftId)) return null;

  const storageKey = `${SALES_DRAFT_STORAGE_PREFIX}${draftId}`;
  try {
    const draft = JSON.parse(storage.getItem(storageKey) || "null");
    if (
      draft?.version !== SALES_DRAFT_VERSION ||
      draft?.id !== draftId ||
      !ALLOWED_SOURCE_TYPES.has(draft?.source_type) ||
      !toPositiveInt(draft?.user_id) ||
      Number(draft?.expires_at) <= now ||
      !draft?.payload ||
      typeof draft.payload !== "object"
    ) {
      storage.removeItem(storageKey);
      return null;
    }
    return draft;
  } catch (_error) {
    storage.removeItem(storageKey);
    return null;
  }
};

export const deleteSalesReplenishmentDraft = (draftId) => {
  const storage = getSessionStorage();
  if (!storage || !isValidDraftId(draftId)) return false;

  try {
    storage.removeItem(`${SALES_DRAFT_STORAGE_PREFIX}${draftId}`);
    return true;
  } catch (_error) {
    return false;
  }
};

export const finalizeSalesReplenishmentDraft = (draftId) => {
  if (!isValidDraftId(draftId)) return null;

  deleteSalesReplenishmentDraft(draftId);
  return draftId;
};

export const getSalesDraftIdFromReplenishment = (replenishment) => {
  const draftId = replenishment?.return_context?.sales_draft_id;
  return isValidDraftId(draftId) ? draftId : null;
};

export const discardSalesDraftFromReplenishment = (replenishment) => {
  const draftId = getSalesDraftIdFromReplenishment(replenishment);
  return draftId ? deleteSalesReplenishmentDraft(draftId) : false;
};

const catalogItemKey = (item) => {
  if (item?.item_type === "service" || item?.service_id) {
    const serviceId = toPositiveInt(item.service_id);
    return serviceId ? `service:${serviceId}` : null;
  }
  const productId = toPositiveInt(
    item?.product_id ?? item?.id_produit ?? item?.produit_id
  );
  return productId ? `product:${productId}` : null;
};

const findCatalogItem = (catalogItems, line) => {
  const key = catalogItemKey(line);
  return (
    (Array.isArray(catalogItems) ? catalogItems : []).find(
      (item) => catalogItemKey(item) === key
    ) || null
  );
};

const mergeShortageIntent = ({ currentItems, shortageItems, catalogItems, kind }) => {
  const itemsByKey = new Map();
  (Array.isArray(currentItems) ? currentItems : []).forEach((item) => {
    const key = catalogItemKey(item);
    if (key) itemsByKey.set(key, { ...item });
  });

  (Array.isArray(shortageItems) ? shortageItems : []).forEach((shortage) => {
    const key = catalogItemKey(shortage);
    const requiredQuantity = toPositiveQuantity(shortage?.required_quantity);
    if (!key || !requiredQuantity) return;

    const existing = itemsByKey.get(key);
    if (existing) {
      const quantityField = kind === "pos" ? "cartQuantity" : "quantite";
      existing[quantityField] = Math.max(
        toPositiveQuantity(existing[quantityField]) || 0,
        requiredQuantity
      );
      if (kind === "sale") existing.quantity = existing.quantite;
      return;
    }

    const catalogItem = findCatalogItem(catalogItems, shortage);
    if (!catalogItem) return;

    if (kind === "pos") {
      itemsByKey.set(key, {
        ...catalogItem,
        cartQuantity: requiredQuantity,
      });
      return;
    }

    itemsByKey.set(key, {
      produit_id: catalogItem.id_produit,
      item_type: catalogItem.item_type || "product",
      service_id: catalogItem.service_id ?? null,
      nom_produit: catalogItem.nom_produit,
      quantite: requiredQuantity,
      prix_unitaire_HT: Number(catalogItem.prix_vente) || 0,
      remise: 0,
      taxe: null,
      montant_taxe: 0,
      quantite_stock: catalogItem.quantite_stock,
      stock_management_mode: catalogItem.stock_management_mode,
      product_type: catalogItem.product_type,
      production_kind: catalogItem.production_kind,
      sellable_without_production: Boolean(
        catalogItem.sellable_without_production
      ),
      product_id:
        catalogItem.item_type === "service"
          ? undefined
          : catalogItem.product_id ?? catalogItem.id_produit,
      product_name: catalogItem.nom_produit,
      quantity: requiredQuantity,
      unit_price: Number(catalogItem.prix_vente) || 0,
      discount_rate: 0,
      tax_id: undefined,
      total_tax: 0,
      subtotal: (Number(catalogItem.prix_vente) || 0) * requiredQuantity,
      unit_id: catalogItem.unit_id ?? catalogItem.unit?.id ?? null,
      unit_name: catalogItem.unit_name || catalogItem.unit?.name,
      unit_short_name:
        catalogItem.unit_short_name || catalogItem.unit?.short_name,
      unit: catalogItem.unit,
    });
  });

  return Array.from(itemsByKey.values());
};

export const mergePosDraftCartWithShortage = (
  cart,
  shortageItems,
  catalogItems
) =>
  mergeShortageIntent({
    currentItems: cart,
    shortageItems,
    catalogItems,
    kind: "pos",
  });

export const mergeManualSaleDraftWithShortage = (
  items,
  shortageItems,
  catalogItems
) =>
  mergeShortageIntent({
    currentItems: items,
    shortageItems,
    catalogItems,
    kind: "sale",
  });

const reconcileDraftItems = (draftItems, catalogItems, kind) => {
  let missingItemCount = 0;
  let priceChangedCount = 0;
  const items = [];

  (Array.isArray(draftItems) ? draftItems : []).forEach((draftItem) => {
    const catalogItem = findCatalogItem(catalogItems, draftItem);
    if (!catalogItem) {
      missingItemCount += 1;
      return;
    }

    const quantity = toPositiveQuantity(
      kind === "pos" ? draftItem.cartQuantity : draftItem.quantite
    );
    if (!quantity) return;

    const currentPrice = Number(catalogItem.prix_vente) || 0;
    const draftPrice = Number(
      kind === "pos" ? draftItem.prix_vente : draftItem.prix_unitaire_HT
    );
    if (Number.isFinite(draftPrice) && Math.abs(draftPrice - currentPrice) > 1e-6) {
      priceChangedCount += 1;
    }

    if (kind === "pos") {
      items.push({
        ...catalogItem,
        cartQuantity: quantity,
        prix_unitaire_HT: currentPrice,
      });
      return;
    }

    items.push({
      ...draftItem,
      produit_id: catalogItem.id_produit,
      item_type: catalogItem.item_type || "product",
      service_id: catalogItem.service_id ?? null,
      nom_produit: catalogItem.nom_produit,
      quantite: quantity,
      prix_unitaire_HT: currentPrice,
      quantite_stock: catalogItem.quantite_stock,
      stock_management_mode: catalogItem.stock_management_mode,
      product_type: catalogItem.product_type,
      production_kind: catalogItem.production_kind,
      sellable_without_production: Boolean(
        catalogItem.sellable_without_production
      ),
      product_id:
        catalogItem.item_type === "service"
          ? undefined
          : catalogItem.product_id ?? catalogItem.id_produit,
      product_name: catalogItem.nom_produit,
      quantity,
      unit_price: currentPrice,
      subtotal: currentPrice * quantity,
      unit_id: catalogItem.unit_id ?? catalogItem.unit?.id ?? null,
      unit_name: catalogItem.unit_name || catalogItem.unit?.name,
      unit_short_name:
        catalogItem.unit_short_name || catalogItem.unit?.short_name,
      unit: catalogItem.unit,
    });
  });

  return { items, missingItemCount, priceChangedCount };
};

export const reconcilePosDraftCart = (draftItems, catalogItems) =>
  reconcileDraftItems(draftItems, catalogItems, "pos");

export const reconcileManualSaleDraftItems = (draftItems, catalogItems) =>
  reconcileDraftItems(draftItems, catalogItems, "sale");
